<template>
  <FormattedMessage
    v-if="estimatedDeliveryDate"
    data-test="delivery-date"
    :definition="translations.singleDeliveryDate"
  >
    <template #weekday>
      <template v-if="isToday(new Date(estimatedDeliveryDate))">
        {{ i18n(translations.today) }}
      </template>
      <SafeTime
        v-else
        :datetime="estimatedDeliveryDate"
        :options="{ weekday: 'long' }"
      />
    </template>
    <template #month>
      <SafeTime
        :datetime="estimatedDeliveryDate"
        :options="{ month: 'short' }"
      />
    </template>
    <template #day>
      <SafeTime
        :datetime="estimatedDeliveryDate"
        :options="{ day: 'numeric' }"
      />
    </template>
  </FormattedMessage>
  <FormattedMessage
    v-else-if="isPromisedDateRangeOnSameMonth"
    data-test="delivery-date"
    :definition="translations.sameMonthDeliveryDateRange"
  >
    <template #month>
      <SafeTime
        :datetime="promisedMinDeliveryDate"
        :options="{ month: 'short' }"
      />
    </template>
    <template #day1>
      <SafeTime
        :datetime="promisedMinDeliveryDate"
        :options="{ day: 'numeric' }"
      />
    </template>
    <template #day2>
      <SafeTime
        :datetime="promisedMaxDeliveryDate"
        :options="{ day: 'numeric' }"
      />
    </template>
  </FormattedMessage>
  <FormattedMessage
    v-else
    data-test="delivery-date"
    :definition="translations.splitMonthDeliveryDateRange"
  >
    <template #month1>
      <SafeTime
        :datetime="promisedMinDeliveryDate"
        :options="{ month: 'short' }"
      />
    </template>
    <template #month2>
      <SafeTime
        :datetime="promisedMaxDeliveryDate"
        :options="{ month: 'short' }"
      />
    </template>
    <template #day1>
      <SafeTime
        :datetime="promisedMinDeliveryDate"
        :options="{ day: 'numeric' }"
      />
    </template>
    <template #day2>
      <SafeTime
        :datetime="promisedMaxDeliveryDate"
        :options="{ day: 'numeric' }"
      />
    </template>
  </FormattedMessage>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { DateTime } from '@backmarket/http-api/src/standards/DateTime'
import SafeTime from '@backmarket/nuxt-layer-base/components/SafeTime/SafeTime.vue'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from './DeliveryDate.translations'

const i18n = useI18n()

const {
  promisedMinDeliveryDate,
  promisedMaxDeliveryDate,
  estimatedDeliveryDate,
} = defineProps<{
  promisedMinDeliveryDate: DateTime
  promisedMaxDeliveryDate: DateTime
  estimatedDeliveryDate: DateTime | null
}>()

const isToday = (date: Date) => {
  const today = new Date()

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

const isPromisedDateRangeOnSameMonth = computed(() => {
  const minDate = new Date(promisedMinDeliveryDate)
  const maxDate = new Date(promisedMaxDeliveryDate)

  return (
    minDate.getMonth() === maxDate.getMonth() &&
    minDate.getFullYear() === maxDate.getFullYear()
  )
})
</script>
